"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  FlexCenter,
  Separator,
  TimelineSkeleton,
} from "@opensea/ui-kit"
import { Suspense, useRef } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { BackButton } from "../../../../../components/BackButton/BackButton"
import { CreateOrdersSummary } from "../../../../../components/CreateOrdersSummary"
import type { CreateOfferFlowFormValues, Offer } from "../../types"
import { convertItemsAndFormValuesToOffers } from "../../utils/constructOffer"
import { CreateOfferFlowTable } from "../CreateOfferFlowTable/CreateOfferFlowTable"
import { CreateOfferTimeline } from "../CreateOfferTimeline/CreateOfferTimeline"
import { CreateOfferFlowReview_itemFragment } from "./CreateOfferFlowReview.fragment"

type CreateOfferFlowReviewProps = {
  items: FragmentOf<typeof CreateOfferFlowReview_itemFragment>[]
  onBack: () => void
  onSuccess?: () => void
  onError?: (error: Error) => void
}

const SEPARATOR = <Separator className="my-1" />

export const CreateOfferFlowReview = ({
  items: itemKeys,
  onBack,
  onSuccess,
  onError,
}: CreateOfferFlowReviewProps) => {
  const t = useTranslations("wallet.CreateOfferFlowReview")
  const { control } = useFormContext<CreateOfferFlowFormValues>()
  const formValues = useWatch({ control }) as CreateOfferFlowFormValues
  const offers = useWatch({ control, name: "offers" })
  const firstOffer = offers[0]

  const items = itemKeys.map(item =>
    readFragment(CreateOfferFlowReview_itemFragment, item),
  )

  const startTime = useRef(new Date())
  const offersRef = useRef<Offer[]>(
    convertItemsAndFormValuesToOffers({
      items,
      formValues: formValues as CreateOfferFlowFormValues,
      startTime: startTime.current,
    }),
  )

  if (!offersRef.current || offersRef.current.length === 0) {
    return null
  }

  const tableItems = offers.flatMap(offer => {
    const matchedItem = items.find(item => item.id === offer.id)
    return matchedItem ? [matchedItem] : []
  })

  return (
    <>
      <BottomSheetHeader>
        <BottomSheetHeaderTitle
          className="cursor-pointer"
          onClick={() => onBack()}
        >
          <FlexCenter className="gap-3">
            <BackButton />
            <Separator className="h-4 text-secondary" orientation="vertical" />
            {t("reviewOffer", { count: items.length })}
          </FlexCenter>
        </BottomSheetHeaderTitle>
      </BottomSheetHeader>
      <BottomSheetBody className="mt-2 pb-0">
        <CreateOfferFlowTable isReview={true} items={tableItems} />

        {SEPARATOR}

        {items[0]?.collection ? (
          <CreateOrdersSummary
            adjustForFees={false}
            collection={items[0].collection}
            creatorFeeEnforced={firstOffer?.isCreatorFeeRequired}
            currencyIdentifier={{
              chainIdentifier: items[0].collection.chain.identifier,
              contractAddress: items[0].collection.chain.offerCurrency.address,
            }}
            isReview
            orderSide="offer"
            orders={offers}
            symbol={items[0].collection.chain.offerCurrency.symbol}
          />
        ) : null}
      </BottomSheetBody>
      <BottomSheetFooter>
        <Suspense
          fallback={<TimelineSkeleton count={offersRef.current.length} />}
        >
          <CreateOfferTimeline
            offers={offersRef.current}
            onBack={onBack}
            onError={onError}
            onSuccess={onSuccess}
          />
        </Suspense>
      </BottomSheetFooter>
    </>
  )
}
